/**
 * Integration Activity API (MyAMP)
 * --------------------------------
 * One record per received email / voice call, with the three outcomes that
 * decide what the user sees: did it become a Command, did it reach the AMS
 * activity log, and was an insured/policy linked.
 *
 * Why this exists: every skip decision is logged server-side, but only to
 * Grafana, which users cannot read. These rows are the same decisions as typed
 * fields so the portal can explain why nothing happened.
 *
 * Auth: the portal's authToken as the Bearer — the user only ever sees their own
 * rows (the server takes user_id from the token, never from a parameter).
 */

const ACTIVITY_API = `${process.env.REACT_APP_MyAMP_Url}/api/integration-activity`;

export type ActivitySource = 'email' | 'voice' | 'sms';

/** 0 = skipped, 1 = ok, 2 = error. Voice leaves ams* null — it never forwards. */
export const STATUS_SKIPPED = 0;
export const STATUS_OK = 1;
export const STATUS_ERROR = 2;

/** 0 = not attempted, 1 = linked, 2 = not found, 3 = error. */
export const LINK_NOT_ATTEMPTED = 0;
export const LINK_LINKED = 1;
export const LINK_NOT_FOUND = 2;
export const LINK_ERROR = 3;

export interface ActivityItem {
  id: number;
  created_at: string | null;
  source: number;
  provider: number | null;
  transaction_id: string | null;

  subject: string | null;
  preview: string | null;
  party_from: string | null;
  party_to: string | null;
  party_name: string | null;
  direction: number | null;
  call_length_secs: number | null;

  integration_id: number | null;
  message_id: string | null;
  thread_id: string | null;
  event_type: string | null;

  command_status: number;
  command_reason: number | null;
  command_detail: string | null;
  command_reason_text: string;
  command_category_id: number | null;
  command_task_id: number | null;

  ams_status: number | null;
  ams_reason: number | null;
  ams_detail: string | null;
  ams_reason_text: string;
  ams_http_status: number | null;

  insured_id: string | null;
  policy_id: string | null;
  link_status: number | null;
  link_detail: string | null;

  process_label: string | null;
  labels: string | null;
}

export interface ActivityPage {
  items: ActivityItem[];
  nextCursorCreatedAt: string | null;
  nextCursorId: number | null;
  retentionDays: number;
}

function authHeaders(): HeadersInit {
  const authToken = localStorage.getItem('authToken') || '';
  return {
    Authorization: `Bearer ${authToken}`,
    'Content-Type': 'application/json',
  };
}

/**
 * A page of the user's activity for one source, newest first.
 * Pass the previous page's cursor values to continue — keyset pagination, so
 * paging stays cheap however many rows exist.
 */
export async function listIntegrationActivity(options: {
  source: ActivitySource;
  onlyProblems?: boolean;
  limit?: number;
  cursorCreatedAt?: string | null;
  cursorId?: number | null;
}): Promise<ActivityPage> {
  const params = new URLSearchParams({ source: options.source });
  if (options.onlyProblems) {
    params.set('only_problems', 'true');
  }
  if (options.limit) {
    params.set('limit', String(options.limit));
  }
  if (options.cursorCreatedAt && options.cursorId) {
    params.set('cursor_created_at', options.cursorCreatedAt);
    params.set('cursor_id', String(options.cursorId));
  }

  const res = await fetch(`${ACTIVITY_API}?${params.toString()}`, {
    headers: authHeaders(),
  });
  if (!res.ok) {
    throw new Error(`Failed to load integration activity: ${res.status}`);
  }
  const data = await res.json();
  return {
    items: Array.isArray(data.items) ? data.items : [],
    nextCursorCreatedAt: data.next_cursor_created_at ?? null,
    nextCursorId: data.next_cursor_id ?? null,
    retentionDays: Number(data.retention_days) || 0,
  };
}
